import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lock, Loader2, CheckCircle2, ShieldOff, Mail,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import {
  isFirebaseConfigured,
  isEmailSignInLink,
  completeEmailSignIn,
  getStoredEmailForSignIn,
  clearStoredEmailForSignIn,
  friendlyFirebaseError,
} from "../lib/firebase";

/**
 * Three modes:
 *  - Firebase email-link callback  → ?via=firebase&apiKey=...&oobCode=...
 *    The URL is a Firebase magic link; we sign in via Firebase, get an
 *    ID token, then POST it + the new password to /api/auth/email-reset.
 *  - Legacy token-based reset     → ?token=XYZ
 *    The old Resend-style flow; POSTs to /api/auth/reset-password.
 *  - Neither                      → "Missing reset token" placeholder.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const legacyToken = params.get("token") || "";

  const [mode, setMode] = useState("loading"); // loading | firebase | legacy | invalid
  const [idToken, setIdToken] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [needEmail, setNeedEmail] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  // On mount, figure out which flow we're in.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const href = typeof window !== "undefined" ? window.location.href : "";
        if (isFirebaseConfigured() && href && isEmailSignInLink(href)) {
          // Firebase magic-link callback.
          const stored = getStoredEmailForSignIn();
          if (stored) {
            try {
              const { idToken: tok, email } = await completeEmailSignIn(stored, href);
              if (cancelled) return;
              setIdToken(tok);
              setVerifiedEmail(email);
              clearStoredEmailForSignIn();
              setMode("firebase");
              return;
            } catch (e) {
              if (cancelled) return;
              const friendly = friendlyFirebaseError(e);
              setErr(friendly || "Couldn't verify the link. Please request a new one.");
              setMode("firebase");
              setNeedEmail(true);
              return;
            }
          }
          // No stored email — ask the user to confirm which email they used.
          if (cancelled) return;
          setMode("firebase");
          setNeedEmail(true);
          return;
        }
        if (legacyToken) {
          if (cancelled) return;
          setMode("legacy");
          return;
        }
        if (cancelled) return;
        setMode("invalid");
      } catch {
        if (cancelled) return;
        setMode("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [legacyToken]);

  const completeWithEnteredEmail = async (e) => {
    e?.preventDefault();
    setErr("");
    const email = (emailInput || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      setErr("Please enter the email you used to request the link.");
      return;
    }
    setBusy(true);
    try {
      const href = window.location.href;
      const { idToken: tok, email: verified } = await completeEmailSignIn(email, href);
      setIdToken(tok);
      setVerifiedEmail(verified);
      clearStoredEmailForSignIn();
      setNeedEmail(false);
    } catch (e2) {
      setErr(friendlyFirebaseError(e2) || "Couldn't verify this link. Please request a new one.");
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (e) => {
    e?.preventDefault();
    setErr("");
    if (pw1.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "firebase") {
        await api.post("/auth/email-reset", {
          firebase_id_token: idToken,
          new_password: pw1,
        }, { retryOnNetworkError: true });
      } else {
        await api.post("/auth/reset-password", {
          token: legacyToken,
          new_password: pw1,
        }, { retryOnNetworkError: true });
      }
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0a0f0d] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative w-full max-w-md"
      >
        <div className="text-center mb-7">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#d4af37] grid place-items-center text-black font-black shadow-lg shadow-amber-500/20">
              8
            </div>
            <span className="text-xs tracking-[0.35em] uppercase text-amber-400/80">
              Cue Lounge
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white">
            Reset password
          </h1>
        </div>

        {mode === "loading" ? (
          <div
            data-testid="reset-loading"
            className="bg-[#121a16] border border-white/10 rounded-2xl p-7 text-center text-gray-400"
          >
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-amber-400" />
            Verifying your reset link…
          </div>
        ) : mode === "invalid" ? (
          <div
            data-testid="reset-no-token"
            className="bg-[#121a16] border border-red-400/30 rounded-2xl p-6 text-center space-y-3"
          >
            <ShieldOff className="w-10 h-10 mx-auto text-red-400" />
            <div className="text-white font-bold">Missing reset link</div>
            <div className="text-sm text-gray-400">
              Open the password-reset link from the email we sent you.
            </div>
            <Link
              to="/login"
              data-testid="reset-back-to-login"
              className="inline-block mt-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest text-sm rounded-md"
            >
              Back to login
            </Link>
          </div>
        ) : done ? (
          <div
            data-testid="reset-success"
            className="bg-[#121a16] border border-emerald-400/30 rounded-2xl p-6 text-center space-y-3"
          >
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400" />
            <div className="text-white font-bold">Password updated!</div>
            <div className="text-sm text-gray-400">
              You can now sign in with your new password.
            </div>
            <Button
              onClick={() => navigate("/login", { replace: true })}
              data-testid="reset-goto-login"
              className="mt-2 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            >
              Go to login
            </Button>
          </div>
        ) : mode === "firebase" && needEmail ? (
          // The user clicked the link on a different device, so we never
          // stored their email in localStorage. Ask them to retype it.
          <form
            onSubmit={completeWithEnteredEmail}
            data-testid="reset-confirm-email-form"
            className="bg-[#121a16] border border-white/10 rounded-2xl p-7 space-y-5"
          >
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                Confirm your email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  data-testid="reset-confirm-email-input"
                  type="email"
                  required
                  autoFocus
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 bg-black/40 border-white/10 text-white"
                />
              </div>
              <p className="text-[11px] text-gray-500 tracking-wide">
                For your security, please re-enter the email you used to
                request this link.
              </p>
            </div>

            {err && (
              <div
                data-testid="reset-error"
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-md"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="reset-confirm-email-submit"
              className="w-full h-11 bg-[#d4af37] hover:bg-[#e2c258] text-black font-bold tracking-widest uppercase rounded-lg"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
            </Button>
          </form>
        ) : (
          <form
            onSubmit={submitNewPassword}
            data-testid="reset-form"
            className="bg-[#121a16] border border-white/10 rounded-2xl p-7 space-y-5"
          >
            {verifiedEmail && (
              <div className="text-center text-xs text-gray-400 -mt-1">
                Resetting password for{" "}
                <span className="text-emerald-300 font-semibold">
                  {verifiedEmail}
                </span>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                New password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  data-testid="reset-new-password"
                  type="password"
                  required
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  placeholder="At least 6 characters"
                  className="pl-10 bg-black/40 border-white/10 text-white"
                  autoFocus
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                Confirm password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  data-testid="reset-confirm-password"
                  type="password"
                  required
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="Re-enter the password"
                  className="pl-10 bg-black/40 border-white/10 text-white"
                />
              </div>
            </div>

            {err && (
              <div
                data-testid="reset-error"
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-md"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-btn"
              className="w-full h-11 bg-[#d4af37] hover:bg-[#e2c258] text-black font-bold tracking-widest uppercase rounded-lg"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>

            <Link
              to="/login"
              className="block text-center text-xs text-gray-500 hover:text-gray-300"
            >
              Back to login
            </Link>
          </form>
        )}
      </motion.div>
    </div>
  );
}
